import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FiGrid, FiMessageSquare, FiPlus, FiX, FiZap, FiBarChart2, FiEdit3, FiRefreshCw, FiUser, FiTrendingUp, FiClock, FiChevronRight, } from 'react-icons/fi';
import { RiSparklingFill } from 'react-icons/ri';
import { useChannels } from '../../context/ChannelContext';
import { useAuth } from '../../features/auth/AuthContext';
import logo from '../../assets/images/M_Multicolor.svg';
import '../../styles/WelcomeChannelPage.css';
function getGreeting(): string {
    const h = new Date().getHours();
    if (h < 12)
        return 'Good morning';
    if (h < 17)
        return 'Good afternoon';
    return 'Good evening';
}
function getTodayLabel(): string {
    return new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
}
const FEATURES = [
    {
        id: 'workforce',
        accent: '#8b5cf6',
        icon: <FiZap size={15}/>,
        title: 'Agentic AI Workforce',
        desc: 'InsuranceAgency.AI deploys multiple specialized AI agents that each handle specific tasks — issuing certificates, processing renewals, analyzing coverage gaps, responding to client inquiries, and more. They work in parallel, around the clock, under your oversight. You stay in control while the work gets done.',
        prompt: 'What does agentic army mean and how do the AI agents work?',
    },
    {
        id: 'os165',
        accent: '#d45cfc',
        icon: <FiBarChart2 size={15}/>,
        title: 'Built on OS165',
        desc: 'OS165 is a mapped model of how independent insurance agencies operate. It defines best-practice workflows across the most common agency functions — from renewals and certificates to submissions and client servicing. Rather than starting from scratch, the AI comes pre-loaded with a real-world understanding of how agencies run. Your agency then customizes on top of that foundation to reflect your own procedures and preferences.',
        prompt: 'What does it mean that it is built on 165 best practices?',
    },
    {
        id: 'learns',
        accent: '#fb923c',
        icon: <FiEdit3 size={15}/>,
        title: 'Learns Your Agency',
        desc: 'InsuranceAgency.AI builds a profile of your agency by analyzing your interactions, preferences, and patterns over time. You can also upload your own SOPs, procedures, and guardrails directly into the platform. Think of it as a private knowledge base that belongs entirely to your agency. The AI references it continuously to understand how your operation runs and adjusts its behavior to match — including the style of communication your team prefers and the workflows you follow.',
        prompt: 'How does the AI learn my agency\'s way of working?',
    },
    {
        id: 'ams',
        accent: '#3ed6b8',
        icon: <FiRefreshCw size={15}/>,
        title: 'AMS-Agnostic — No Rip and Replace',
        desc: 'InsuranceAgency.AI is AMS-agnostic. It works alongside your existing platform whether that is Momentum or any other AMS. There is no migration, no data transfer headache, and no disruption to how your team currently works. It sits on top of your AMS and makes it significantly more powerful by adding AI-driven automation, intelligent workflows, and a conversational interface — the intelligence layer your AMS never had.',
        prompt: 'Do I have to replace my AMS or switch systems to use InsuranceAgency.AI?',
    },
    {
        id: 'control',
        accent: '#5aaeff',
        icon: <FiUser size={15}/>,
        title: 'Human-in-the-Loop Control',
        desc: 'InsuranceAgency.AI is built around a human-in-the-loop model. There are checkpoints where the AI pauses and waits for your approval before proceeding. You also define the guardrails — what the AI is allowed to do, what requires sign-off, and what is off limits entirely. The platform logs all AI activity, supports human review and override at every step. You are always in control.',
        prompt: 'Can I control or override what the AI does?',
    },
    {
        id: 'scale',
        accent: '#f5c842',
        icon: <FiTrendingUp size={15}/>,
        title: 'Scale Without Headcount',
        desc: 'InsuranceAgency.AI lets your existing team take on more volume by offloading routine work to AI agents. Certificate requests, renewal prep, coverage analysis, client follow-up — the AI handles the workload that would otherwise require additional staff. You grow your capacity without growing your payroll. Agencies that adopt AI tools consistently outperform those that do not — and the gap is widening.',
        prompt: 'Can this help me scale without hiring more staff?',
    },
];
const STATS = [
    { num: '30+', label: 'Live AI Tools', icon: <FiZap size={16}/>, accent: '#8b5cf6' },
    { num: '165', label: 'Best-Practice Workflows', icon: <FiBarChart2 size={16}/>, accent: '#d45cfc' },
    { num: '24/7', label: 'Autonomous Agents', icon: <FiClock size={16}/>, accent: '#3ed6b8' },
    { num: '0', label: 'Systems to Replace', icon: <FiRefreshCw size={16}/>, accent: '#5aaeff' },
];
const MESSAGES = [
    { id: 'm1', role: 'user', text: "Can you process the renewal for Acme Corp? It's due in 14 days.", time: '9:41 am' },
    { id: 'm2', role: 'assistant', text: "On it. I've pulled Acme Corp's current coverage, identified a $200K gap on their commercial auto, and flagged two carriers with better rates. Renewal summary ready for your review.", time: '9:41 am' },
    { id: 'm3', role: 'user', text: 'Send a COI to their landlord — same as last year.', time: '9:42 am' },
    { id: 'm4', role: 'assistant', text: 'Certificate issued and emailed to landlord@acmecorp.com. Audit trail updated. Anything else on this account?', time: '9:42 am' },
];
const CHANNEL_BENEFITS = [
    { icon: <FiMessageSquare size={15}/>, text: 'Dedicated AI conversations' },
    { icon: <FiGrid size={15}/>, text: 'Multiple Skills per workspace' },
    { icon: <RiSparklingFill size={15}/>, text: 'Full history, always saved' },
];
interface WelcomeChannelPageProps {
    onPromptClick: (prompt: string) => void;
    onCreateChannel: () => void;
    onDismiss: () => void;
}
const WelcomeChannelPage: React.FC<WelcomeChannelPageProps> = ({ onPromptClick, onCreateChannel, onDismiss }) => {
    const { channels } = useChannels();
    const { user } = useAuth();
    const [confirming, setConfirming] = useState(false);
    const hasCustomChannels = channels.some(c => c.name?.toLowerCase() !== 'welcome');
    const userName = user?.name
        || [localStorage.getItem('firstName'), localStorage.getItem('lastName')].filter(Boolean).join(' ')
        || localStorage.getItem('userName')
        || '';
    return (<div className="wcp">

      <motion.div className="wcp__left" initial={{ opacity: 0, y: 22 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: [0.23, 1, 0.32, 1] }}>
      
        <div className="wcp__topbar">
          <div className="wcp__topbar-left">
            <div className="wcp__logo">
              <img src={logo} alt="Insurance Agency AI"/>
              <div className="wcp__logo-ring"/>
            </div>
            <div>
              <h1 className="wcp__greeting">
                {getGreeting()}{userName ? `, ${userName}` : ''}
              </h1>
              <p className="wcp__date">{getTodayLabel()}</p>
            </div>
          </div>
          <div className="wcp__badge">
            <span className="wcp__badge-dot"/>
            <RiSparklingFill size={12}/>
            Ask me anything
          </div>
        </div>

        <p className="wcp__intro">
          InsuranceAgency.AI is the first fully AI-powered operating system built specifically for independent insurance agencies. It deploys a digital workforce of AI agents to handle the repetitive, time-consuming tasks that slow your agency down — certificate issuance, renewals, coverage analysis, client questions, and more. You and your team get to focus on what actually grows your business: building client relationships and providing expert advice.
        </p>

        <div className="wcp__features">
          {FEATURES.map((f, i) => (<motion.button key={f.id} className="wcp__feature" style={{ '--wcp-accent': f.accent } as React.CSSProperties} onClick={() => onPromptClick(f.prompt)} initial={{ opacity: 0, x: -12 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.1 + i * 0.06, duration: 0.35 }} whileHover={{ x: 4 }}>
              <div className="wcp__feature-bar"/>
              <div className="wcp__feature-icon-circle">
                {f.icon}
              </div>
              <div className="wcp__feature-body">
                <span className="wcp__feature-title">{f.title}</span>
                <span className="wcp__feature-desc">{f.desc}</span>
              </div>
              <FiChevronRight size={14} className="wcp__feature-arrow"/>
            </motion.button>))}
        </div>

      </motion.div>

      <motion.div className="wcp__right" initial={{ opacity: 0, y: 22 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1, ease: [0.23, 1, 0.32, 1] }}>
        {!hasCustomChannels && (<motion.div className="wcp__new-channel" initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.55, ease: [0.23, 1, 0.32, 1] }}>
            <div className="wcp__nc-border"/>

            <div className="wcp__nc-top">
              <div className="wcp__nc-icon">
                <FiGrid size={18} strokeWidth={2.5}/>
              </div>
              <div className="wcp__nc-title-wrap">
                <h2 className="wcp__nc-heading">Create Your First Workspace</h2>
                <p className="wcp__nc-desc">Focused workspaces with dedicated AI agents and organised history.</p>
              </div>
              <motion.button className="wcp__nc-btn" onClick={onCreateChannel} whileHover={{ scale: 1.05, y: -2 }} whileTap={{ scale: 0.96 }}>
                <FiPlus size={13} strokeWidth={2.5}/>
                Create
              </motion.button>
            </div>

            <div className="wcp__nc-divider"/>

            <div className="wcp__nc-pills">
              {CHANNEL_BENEFITS.map((b, i) => (<motion.div key={i} className="wcp__nc-pill" initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.72 + i * 0.08, duration: 0.28 }}>
                  <span className="wcp__nc-pill-icon">{b.icon}</span>
                  <span>{b.text}</span>
                </motion.div>))}
            </div>
          </motion.div>)}

        {hasCustomChannels && (!confirming ? (<motion.div key="dismiss-idle" className="wcp__dismiss-card" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }} transition={{ duration: 0.35, delay: 0.45, ease: [0.23, 1, 0.32, 1] }}>
            <div className="wcp__dismiss-icon">
              <FiX size={15} strokeWidth={2.5}/>
            </div>
            <div className="wcp__dismiss-body">
              <span className="wcp__dismiss-title">Already know your way around?</span>
              <span className="wcp__dismiss-desc">Hide this welcome screen so you go straight to chat every time you open this channel.</span>
            </div>
            <button className="wcp__dismiss-btn" onClick={() => setConfirming(true)}>
              Don't show again
            </button>
          </motion.div>) : (<motion.div key="dismiss-confirm" className="wcp__dismiss-card wcp__dismiss-card--confirming" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }} transition={{ duration: 0.28, ease: [0.23, 1, 0.32, 1] }}>
            <div className="wcp__dismiss-confirm-body">
              <span className="wcp__dismiss-title">Hide this welcome page?</span>
              <span className="wcp__dismiss-desc">You won't see this screen again on this browser. You can always reset it from Settings.</span>
            </div>
            <div className="wcp__dismiss-actions">
              <button className="wcp__dismiss-cancel" onClick={() => setConfirming(false)}>
                Cancel
              </button>
              <button className="wcp__dismiss-confirm" onClick={onDismiss}>
                Yes, hide it
              </button>
            </div>
          </motion.div>))}

        <div className="wcp__chat-wrap">

          <div className="wcp__chat">
            <div className="wcp__chat-chrome">
              <div className="wcp__chrome-dots">
                <span className="wcp__dot wcp__dot--red"/>
                <span className="wcp__dot wcp__dot--yellow"/>
                <span className="wcp__dot wcp__dot--green"/>
              </div>
              <div className="wcp__chrome-center">
                <span className="wcp__chrome-status-dot"/>
                <span className="wcp__chrome-label">Insurance Agency AI</span>
              </div>
              <div className="wcp__chrome-right">
                <RiSparklingFill size={11} style={{ color: '#c4b5fd', opacity: 0.7 }}/>
              </div>
            </div>


            <div className="wcp__chat-body">
              {MESSAGES.map((msg, i) => (<motion.div key={msg.id} className={`wcp__msg wcp__msg--${msg.role}`} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 + i * 0.28, duration: 0.3 }}>
                  {msg.role === 'assistant' && (<div className="wcp__avatar wcp__avatar--ai">
                      <RiSparklingFill size={11}/>
                    </div>)}
                  <div className="wcp__msg-col">
                    <div className="wcp__msg-meta">
                      <span className="wcp__msg-name">{msg.role === 'user' ? 'You' : 'Insurance Agency AI'}</span>
                      <span className="wcp__msg-time">{msg.time}</span>
                    </div>
                    <div className="wcp__bubble">{msg.text}</div>
                  </div>
                  {msg.role === 'user' && (<div className="wcp__avatar wcp__avatar--user">D</div>)}
                </motion.div>))}
            </div>



            <div className="wcp__chat-input">
              <span className="wcp__chat-slash">/</span>
              <span className="wcp__chat-placeholder">Message Insurance Agency AI…</span>
              <div className="wcp__chat-send">
                <FiChevronRight size={13}/>
              </div>
            </div>
          </div>
        </div>

        <div className="wcp__stats">
          {STATS.map((s, i) => (<motion.div key={s.label} className="wcp__stat" style={{ '--wcp-kpi-accent': s.accent } as React.CSSProperties} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 + i * 0.08, duration: 0.35 }}>
              <div className="wcp__stat-glow"/>
              <div className="wcp__stat-icon-circle">{s.icon}</div>
              <div className="wcp__stat-text">
                <span className="wcp__stat-num">{s.num}</span>
                <span className="wcp__stat-label">{s.label}</span>
              </div>
            </motion.div>))}
        </div>



      </motion.div>
    </div>);
};
export default WelcomeChannelPage;
